import { useMemo } from 'react'

import type { FormSchema } from '@fv/client-core'
import { isCarrierPackaging } from '@fv/models'

import {
  useAreDimsRequired,
  useIsNmfcRequired,
} from '@/features/settings/account-settings/hooks'

import { validateNmfc } from '../commodityUtils'
import { useItemsState } from './loadItemsContext'
import type { LoadHandlingUnitFormModel, LoadItemFormModel } from './types'

type HandlingUnitSchemaOpts = {
  requireDims: boolean
  isTemplate?: boolean
  requireNmfc?: boolean
}
export const shouldRequireDims = (
  v: Pick<LoadHandlingUnitFormModel, 'width' | 'height' | 'length' | 'type'>,
  opts: HandlingUnitSchemaOpts,
) =>
  (opts.requireDims && !isCarrierPackaging(v.type)) ||
  !!(v.width || v.height || v.length)

const createLoadHandlingUnitSchema = (
  opts: HandlingUnitSchemaOpts,
): FormSchema<LoadHandlingUnitFormModel> => {
  const requireDims = (v: LoadHandlingUnitFormModel) =>
    !opts.isTemplate && shouldRequireDims(v, opts)
  const required = () => !opts.isTemplate

  return {
    stackable: {
      valueAsChecked: true,
      readOnly: v => v.readOnly?.stackable ?? false,
    },
    nonStandard: {
      valueAsChecked: true,
      readOnly: v =>
        v.readOnly?.nonStandard ?? (false || isCarrierPackaging(v.type)),
    },
    quantity: {
      min: 1,
      valueAsNumber: true,
      readOnly: v => v.readOnly?.pieces ?? false,
      required,
    },
    type: {
      readOnly: v => v.readOnly?.package ?? false,
    },
    weight: {
      valueAsNumber: true,
      min: 1,
      readOnly: v => v.readOnly?.weight ?? false,
      required,
    },
    height: {
      min: 1,
      valueAsNumber: true,
      readOnly: v =>
        (v.readOnly?.height ?? false) || isCarrierPackaging(v.type),
      required: requireDims,
    },
    width: {
      min: 1,
      valueAsNumber: true,
      readOnly: v => (v.readOnly?.width ?? false) || isCarrierPackaging(v.type),
      required: requireDims,
    },
    length: {
      min: 1,
      valueAsNumber: true,
      readOnly: v =>
        (v.readOnly?.length ?? false) || isCarrierPackaging(v.type),
      required: requireDims,
    },
    fullNmfc: {
      readOnly: v => v.readOnly?.nmfc ?? false,
      required: () => !!opts.requireNmfc,
      validate: validateNmfc,
    },
    isHazardous: {
      valueAsChecked: true,
    },
  }
}

export const LoadItemSchema: FormSchema<LoadItemFormModel> = {
  quantity: {
    min: 1,
    valueAsNumber: true,
    required: v => !!v.type,
  },
  isHazardous: {
    valueAsChecked: true,
  },
}

export const useLoadHandlingUnitSchema = () => {
  const { workflow, isTemplate } = useItemsState()
  const requireDims = useAreDimsRequired(workflow)
  const requireNmfc = useIsNmfcRequired(workflow)
  return useMemo(
    () =>
      createLoadHandlingUnitSchema({
        requireDims,
        isTemplate,
        requireNmfc,
      }),
    [requireDims, isTemplate, requireNmfc],
  )
}
